import React, { FC, useState } from "react";
import { NavLink } from "react-router-dom";

import { Contact } from "../../types/ContactTypes";
import {
  Avatar,
  BottomSheet,
  Button,
  Icon,
  ListControl,
  Pill,
} from "../common";
import { STATUS_CONFIG } from "../../constants";
import { StatusSet } from "../../types/GlobalTypes";
import { AccountType } from "../../types/UserTypes";
import { ROUTES } from "../../routes";
import { Popover } from "react-tiny-popover";
import { ContactsTableDropdown } from "../menu/ContactsTableDropdown";
import { isMobileMedia } from "../../utils/media";

const STATUS_PROPS = STATUS_CONFIG[StatusSet.CONTACT];

type Props = {
  data: Contact[];
  reload?: () => void;
};

export const ContactsTable: FC<Props> = ({ data, reload }) => {
  const [selected, setSelected] = useState<Contact | undefined>(undefined);

  return (
    <>
      <div
        className="w-full flex flex-col items-stretch"
        data-test="invoices-table"
      >
        {data.map((contact) => (
          <ListControl
            key={contact.id}
            className="flex flex-col min-w-full items-stretch justify-stretch -mx-4"
            onClick={() => setSelected(contact)}
          >
            <NavLink
              to={ROUTES.CONTACTS.replace(":id", contact.id)}
              className="border-t border-gray-50 flex flex-row w-full max-w-full items-center justify-between gap-x-8 hover:bg-slate-100 py-6 px-4"
              data-test="invoice-table-row"
            >
              <Avatar
                type="business"
                title={contact.contact_name}
                className="flex-none"
                icon={
                  isMobileMedia()
                    ? STATUS_PROPS[contact.status].icon
                    : undefined
                }
                iconColor={
                  isMobileMedia()
                    ? STATUS_PROPS[contact.status].color
                    : undefined
                }
              />

              <div className="mr-auto flex flex-col gap-y-2 flex-auto max-w-[380px]">
                <div
                  className="theme-text-default w-full overflow-hidden text-ellipsis"
                  data-test="contact-name"
                >
                  {contact.contact_name}
                </div>

                <div className="theme-text-secondary" data-test="account-type">
                  {contact.account_type === AccountType.CONTRACTOR
                    ? "Individual"
                    : "Company"}
                </div>
              </div>

              <div
                className="hidden md:block w-36 flex-none"
                data-test="contact-status"
              >
                <Pill {...STATUS_PROPS[contact.status]} />
              </div>

              {!isMobileMedia() && (
                <Popover
                  isOpen={selected?.id === contact.id}
                  positions={["bottom", "left"]}
                  content={
                    <ContactsTableDropdown
                      data={contact}
                      closeDropdown={() => setSelected(undefined)}
                      onErrorRetry={reload}
                    />
                  }
                  padding={10}
                  align="end"
                  onClickOutside={() => setSelected(undefined)}
                >
                  <div className="relative flex-none hidden md:block">
                    <Button
                      data-test="invoice-three-dots-options-btn"
                      variant={Button.variant.TERTIARY}
                      onClick={() => setSelected(contact)}
                      icon
                    >
                      <Icon icon="Dot-Menu" size={24} disableFill />
                    </Button>
                  </div>
                </Popover>
              )}
            </NavLink>
          </ListControl>
        ))}
      </div>

      {isMobileMedia() && (
        <BottomSheet
          isOpen={selected !== undefined}
          close={() => setSelected(undefined)}
        >
          {selected && (
            <ContactsTableDropdown
              data={selected}
              closeDropdown={() => setSelected(undefined)}
              onErrorRetry={reload}
              popover={false}
            />
          )}
        </BottomSheet>
      )}
    </>
  );
};
